import { ValuesCache } from '@leverege/value-cache'

export default class ComplexPasswordValidator {
  static MODEL_DEFAULTS = {
    requireUpper : true,
    requireLower : true,
    requireNumber : true,
    requireSpecial : true,
    specialChars : '-+*()^%$#@!~',
    maxLength : 64,
    minLength : 8,
    expiresAfter : 'never',
    history : 0,
    lockout : {
      after : -1,
    },
  }

  static UPPERCASE_REGEX = /[A-Z]/g
  static LOWERCASE_REGEX = /[a-z]/g
  static NUMBER_REGEX = /[0-9]/g

  // Requirement Codes
  static REQUIREMENT_MIN_LENGTH = 'api-validator.login.requirement.minLength'
  static REQUIREMENT_MAX_LENGTH = 'api-validator.login.requirement.maxLength'
  static REQUIREMENT_UPPER = 'api-validator.login.requirement.upper'
  static REQUIREMENT_LOWER = 'api-validator.login.requirement.lower'
  static REQUIREMENT_NUMBER = 'api-validator.login.requirement.number'
  static REQUIREMENT_SPECIAL = 'api-validator.login.requirement.special'

  constructor( model ) {
    const { type, ...rest } = model || {}

    this.model = {
      type,
      ...ComplexPasswordValidator.MODEL_DEFAULTS,
      ...rest
    }

    this.cache = new ValuesCache()
  }

  validate( value ) {
    return this.cache.calc( 'valid', ( val, model ) => {
      let valid = true
      const errors = {}

      if ( typeof model.minLength === 'number' ) {
        const res = this.isMinLength( val )

        errors[ComplexPasswordValidator.REQUIREMENT_MIN_LENGTH] = {
          type : ComplexPasswordValidator.REQUIREMENT_MIN_LENGTH,
          valid : res,
          count : model.minLength
        }

        if ( !res ) {
          valid = false
        }
      }

      if ( typeof model.maxLength === 'number' ) {
        const res = this.isMaxLength( val )

        errors[ComplexPasswordValidator.REQUIREMENT_MAX_LENGTH] = {
          type : ComplexPasswordValidator.REQUIREMENT_MAX_LENGTH,
          valid : res,
          count : model.maxLength
        }

        if ( !res ) {
          valid = false
        }
      }

      if ( model.requireUpper != null && model.requireUpper !== false ) {
        const res = this.hasUpper( val )

        errors[ComplexPasswordValidator.REQUIREMENT_UPPER] = {
          type : ComplexPasswordValidator.REQUIREMENT_UPPER,
          valid : res,
          count : typeof model.requireUpper === 'number' ? model.requireUpper : 1
        }

        if ( !res ) {
          valid = false
        }
      }

      if ( model.requireLower != null && model.requireLower !== false ) {
        const res = this.hasLower( val )

        errors[ComplexPasswordValidator.REQUIREMENT_LOWER] = {
          type : ComplexPasswordValidator.REQUIREMENT_LOWER,
          valid : res,
          count : typeof model.requireLower === 'number' ? model.requireLower : 1
        }

        if ( !res ) {
          valid = false
        }
      }

      if ( model.requireNumber != null && model.requireNumber !== false ) {
        const res = this.hasNumber( val )

        errors[ComplexPasswordValidator.REQUIREMENT_NUMBER] = {
          type : ComplexPasswordValidator.REQUIREMENT_NUMBER,
          valid : res,
          count : typeof model.requireNumber === 'number' ? model.requireNumber : 1
        }

        if ( !res ) {
          valid = false
        }
      }

      if ( model.requireSpecial != null && model.requireSpecial !== false && model.specialChars ) {
        const res = this.hasSpecial( val )

        errors[ComplexPasswordValidator.REQUIREMENT_SPECIAL] = {
          type : ComplexPasswordValidator.REQUIREMENT_SPECIAL,
          valid : res,
          count : typeof model.requireSpecial === 'number' ? model.requireSpecial : 1,
          charSet : model.specialChars
        }

        if ( !res ) {
          valid = false
        }
      }

      return {
        valid,
        errors
      }
    }, value, this.model )
  }

  getRequirements() {
    return this.cache.calc( 'requirements', ( model ) => {
      const requirements = {}
  
      if ( typeof model.minLength === 'number' ) {
        requirements[ComplexPasswordValidator.REQUIREMENT_MIN_LENGTH] = {
          type : ComplexPasswordValidator.REQUIREMENT_MIN_LENGTH,
          count : model.minLength
        }
      }
  
      if ( typeof model.maxLength === 'number' ) {
        requirements[ComplexPasswordValidator.REQUIREMENT_MAX_LENGTH] = {
          type : ComplexPasswordValidator.REQUIREMENT_MAX_LENGTH,
          count : model.maxLength
        }
      }
  
      if ( model.requireUpper != null && model.requireUpper !== false ) {
        requirements[ComplexPasswordValidator.REQUIREMENT_UPPER] = {
          type : ComplexPasswordValidator.REQUIREMENT_UPPER,
          count : typeof model.requireUpper === 'number' ? model.requireUpper : 1
        }
      }
  
      if ( model.requireLower != null && model.requireLower !== false ) {
        requirements[ComplexPasswordValidator.REQUIREMENT_LOWER] = {
          type : ComplexPasswordValidator.REQUIREMENT_LOWER,
          count : typeof model.requireLower === 'number' ? model.requireLower : 1
        }
      }
  
      if ( model.requireNumber != null && model.requireNumber !== false ) {
        requirements[ComplexPasswordValidator.REQUIREMENT_NUMBER] = {
          type : ComplexPasswordValidator.REQUIREMENT_NUMBER,
          count : typeof model.requireNumber === 'number' ? model.requireNumber : 1
        }
      }
  
      if ( model.requireSpecial != null && model.requireSpecial !== false && model.specialChars ) {
        requirements[ComplexPasswordValidator.REQUIREMENT_SPECIAL] = {
          type : ComplexPasswordValidator.REQUIREMENT_SPECIAL,
          count : typeof model.requireSpecial === 'number' ? model.requireSpecial : 1,
          charSet : model.specialChars
        }
      }
  
      return requirements
    }, this.model )
  }

  isMinLength( value ) {
    if ( !value ) {
      return false
    }

    return value.length >= this.model.minLength
  }

  isMaxLength( value ) {
    if ( !value ) {
      return false
    }

    return value.length <= this.model.maxLength
  }

  hasUpper( value ) {
    if ( !value ) {
      return false
    }

    const upperCount = typeof this.model.requireUpper === 'number' ?
      this.model.requireUpper :
      1
    
    return value.match( ComplexPasswordValidator.UPPERCASE_REGEX )?.length >= upperCount || false
  }

  hasLower( value ) {
    if ( !value ) {
      return false
    }

    const lowerCount = typeof this.model.requireLower === 'number' ?
      this.model.requireLower :
      1

    return value.match( ComplexPasswordValidator.LOWERCASE_REGEX )?.length >= lowerCount || false
  }

  hasNumber( value ) {
    if ( !value ) {
      return false
    }

    const numberCount = typeof this.model.requireNumber === 'number' ?
      this.model.requireNumber :
      1

    return value.match( ComplexPasswordValidator.NUMBER_REGEX )?.length >= numberCount || false
  }

  hasSpecial( value ) {
    if ( !value ) {
      return false
    }

    const specialCount = typeof this.model.requireSpecial === 'number' ?
      this.model.requireSpecial :
      1
    let totalSpecials = 0
    for ( let i = 0; i < this.model.specialChars.length; i++ ) {
      const char = this.model.specialChars.charAt( i )

      if ( value.includes( char ) ) {
        totalSpecials += 1
      }

      if ( totalSpecials >= specialCount ) {
        return true
      }
    }

    return false
  }  
}
